import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LeadsProvider } from './context/LeadsContext';
import { UserPreferencesProvider } from './context/UserPreferencesContext';
import { Login } from './components/Login/Login';
import { Dashboard } from './pages/Dashboard/Dashboard';
import { LeadDetails } from './pages/LeadDetails/LeadDetails';
import { Landing } from './pages/Landing/Landing';
import { Pricing } from './pages/Pricing/Pricing';
import { Register } from './pages/Register/Register';
import { Settings } from './pages/Settings/Settings';
import { AtendenteSdrConsole } from './pages/IaConsole/AtendenteSdrConsole';
import { Admin } from './pages/Admin/Admin';
import { StatsDashboard } from './pages/StatsDashboard/StatsDashboard';
import { ContatosRh } from './pages/ContatosRh/ContatosRh';

import { CheckoutSuccess, CheckoutCancel } from './pages/Checkout';
import { AcceptInvite } from './pages/AcceptInvite';
import { Unsubscribe } from './pages/Unsubscribe/Unsubscribe';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import './App.css';

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Carregando...</div>;
  }

  return isAuthenticated ? (
    <UserPreferencesProvider>
      <LeadsProvider>{children}</LeadsProvider>
    </UserPreferencesProvider>
  ) : (
    <Navigate to="/login" />
  );
};

const SuperAdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, isSuperAdmin, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Carregando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const ManagerRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAdmin, isGerente, isSuperAdmin } = useAuth();

  if (!isAdmin && !isGerente && !isSuperAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
      <Route path="/u/:code" element={<Unsubscribe />} />
      
      {/* Super Admin Route */}
      <Route
        path="/admin"
        element={
          <SuperAdminRoute>
            <Admin />
          </SuperAdminRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/leads/:leadId"
        element={
          <PrivateRoute>
            <LeadDetails />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
      {/* OpenSpec #2.5 ia-console-ui-shell */}
      <Route
        path="/configuracao/ia/atendente-sdr"
        element={
          <PrivateRoute>
            <AtendenteSdrConsole />
          </PrivateRoute>
        }
      />
      <Route
        path="/stats-dashboard"
        element={
          <PrivateRoute>
            <ManagerRoute>
              <StatsDashboard />
            </ManagerRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/contatos-rh"
        element={
          <PrivateRoute>
            <ContatosRh />
          </PrivateRoute>
        }
      />
      <Route
        path="/checkout/success"
        element={
          <PrivateRoute>
            <CheckoutSuccess />
          </PrivateRoute>
        }
      />
      <Route
        path="/checkout/cancel"
        element={
          <PrivateRoute>
            <CheckoutCancel />
          </PrivateRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
